import { useEffect, useState } from 'react';
import { apiGet, apiPut } from '@/lib/api-client';

export interface BrandingConfig {
  logoUrl: string;
  faviconUrl: string;
  appName: string;
  tagline: string;
}

const DEFAULT_BRANDING: BrandingConfig = {
  logoUrl: 'https://r2.levelupcoach.app/logo/lvl10.app%20(1024%20x%201024%20px)/Level%20Up%20Fire%20Gradient%20White%20Txt%20500x500.png',
  faviconUrl: '/favicon.svg',
  appName: 'LevelUp Coach',
  tagline: 'Faith. Family. Finance. Fitness.',
};

export function useBranding() {
  const [branding, setBranding] = useState<BrandingConfig>(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    loadBranding();
  }, []);

  async function loadBranding() {
    try {
      setLoading(true);
      setError(null);

      // Fetch branding settings from API
      const data = await apiGet<Record<string, any>>(
        '/api/public/settings?keys=branding.logo_url,branding.favicon_url,branding.app_name,branding.tagline'
      );

      // Parse settings into BrandingConfig
      const config: Partial<BrandingConfig> = {};

      if (data['branding.logo_url']) {
        config.logoUrl = data['branding.logo_url']?.url;
      }
      if (data['branding.favicon_url']) {
        config.faviconUrl = data['branding.favicon_url']?.url;
        // Update favicon dynamically
        if (config.faviconUrl) {
          updateFavicon(config.faviconUrl);
        }
      }
      if (data['branding.app_name']) {
        config.appName = data['branding.app_name']?.name;
      }
      if (data['branding.tagline']) {
        config.tagline = data['branding.tagline']?.tagline;
      }

      setBranding((prev) => ({ ...prev, ...config }));
    } catch (err) {
      console.error('Error loading branding:', err);
      setError(err instanceof Error ? err : new Error('Failed to load branding'));
      // Keep default branding on error
    } finally {
      setLoading(false);
    }
  }

  function updateFavicon(url: string) {
    try {
      let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;

      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }

      link.href = url;
    } catch (error) {
      console.error('Error updating favicon:', error);
    }
  }

  async function updateBranding(updates: Partial<BrandingConfig>) {
    try {
      // Prepare updates for API
      const settingsToUpdate: Record<string, any> = {};

      if (updates.logoUrl !== undefined) {
        settingsToUpdate['branding.logo_url'] = { url: updates.logoUrl };
      }

      if (updates.faviconUrl !== undefined) {
        settingsToUpdate['branding.favicon_url'] = { url: updates.faviconUrl };
      }

      if (updates.appName !== undefined) {
        settingsToUpdate['branding.app_name'] = { name: updates.appName };
      }

      if (updates.tagline !== undefined) {
        settingsToUpdate['branding.tagline'] = { tagline: updates.tagline };
      }

      // Update all settings at once
      await apiPut('/api/admin/settings', { settings: settingsToUpdate });

      // Reload branding
      await loadBranding();
    } catch (err) {
      console.error('Error updating branding:', err);
      throw err;
    }
  }

  return {
    branding,
    loading,
    error,
    updateBranding,
    refresh: loadBranding,
  };
}
